/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ActivityLogDto } from '../models/activity-log-dto';
import { CommonId } from '../models/common-id';
import { ExcelUploadRequestDto } from '../models/excel-upload-request-dto';
import { MessageDto } from '../models/message-dto';
import { SubscriptionDto } from '../models/subscription-dto';
import { SubscriptionMetadataDto } from '../models/subscription-metadata-dto';


/**
 * Processing of Infra Billing files to caluculate the monthly bill amount
 */
@Injectable({
  providedIn: 'root',
})
export class InfraBillingService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation reviewExcelOk
   */
  static readonly ReviewExcelOkPath = '/api/v1/excel/reviewOk';

  /**
   * Billing Excel Review.
   *
   * The API updates the status to review of billing excel
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `reviewExcelOk()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewExcelOk$Response(params: {
    orderId: string;
    month: number;
    year: number;
    comments?: string;
  }): Observable<StrictHttpResponse<MessageDto>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.ReviewExcelOkPath, 'put');
    if (params) {
      rb.query('orderId', params.orderId, {});
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
      rb.query('comments', params.comments, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MessageDto>;
      })
    );
  }

  /**
   * Billing Excel Review.
   *
   * The API updates the status to review of billing excel
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `reviewExcelOk$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewExcelOk(params: {
    orderId: string;
    month: number;
    year: number;
    comments?: string;
  }): Observable<MessageDto> {

    return this.reviewExcelOk$Response(params).pipe(
      map((r: StrictHttpResponse<MessageDto>) => r.body as MessageDto)
    );
  }

  /**
   * Path part for operation reviewOk1
   */
  static readonly ReviewOk1Path = '/api/v1/cloud-excel/review-ok';

  /**
   * Billing Excel Review.
   *
   * The API updates the status to review of billing excel
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `reviewOk1()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewOk1$Response(params: {
    cloudId: string;
    month: number;
    year: number;
    comments?: string;
  }): Observable<StrictHttpResponse<MessageDto>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.ReviewOk1Path, 'put');
    if (params) {
      rb.query('cloudId', params.cloudId, {});
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
      rb.query('comments', params.comments, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MessageDto>;
      })
    );
  }

  /**
   * Billing Excel Review.
   *
   * The API updates the status to review of billing excel
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `reviewOk1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewOk1(params: {
    cloudId: string;
    month: number;
    year: number;
    comments?: string;
  }): Observable<MessageDto> {

    return this.reviewOk1$Response(params).pipe(
      map((r: StrictHttpResponse<MessageDto>) => r.body as MessageDto)
    );
  }

  /**
   * Path part for operation uploadBillingExcelToCloud
   */
  static readonly UploadBillingExcelToCloudPath = '/api/v1/excel/upload';

  /**
   * Upload Billing Excel.
   *
   * The API uploads billing excel to azure storage account
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadBillingExcelToCloud()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadBillingExcelToCloud$Response(params?: {
    body?: ExcelUploadRequestDto
  }): Observable<StrictHttpResponse<SubscriptionMetadataDto>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.UploadBillingExcelToCloudPath, 'post');
    if (params) {
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SubscriptionMetadataDto>;
      })
    );
  }

  /**
   * Upload Billing Excel.
   *
   * The API uploads billing excel to azure storage account
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `uploadBillingExcelToCloud$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadBillingExcelToCloud(params?: {
    body?: ExcelUploadRequestDto
  }): Observable<SubscriptionMetadataDto> {

    return this.uploadBillingExcelToCloud$Response(params).pipe(
      map((r: StrictHttpResponse<SubscriptionMetadataDto>) => r.body as SubscriptionMetadataDto)
    );
  }

  /**
   * Path part for operation uploadConsolidatedExcel
   */
  static readonly UploadConsolidatedExcelPath = '/api/v1/cloud-excel/upload';

  /**
   * Upload Billing Excel.
   *
   * The API uploads billing excel to azure storage account
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadConsolidatedExcel()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  uploadConsolidatedExcel$Response(params: {
    month?: number;
    year?: number;
    body?: {
      cloudId: string;
      fromCurrencyId?: string;
      comments?: string;
'file': Blob;
}
  }): Observable<StrictHttpResponse<SubscriptionMetadataDto>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.UploadConsolidatedExcelPath, 'post');
    if (params) {
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SubscriptionMetadataDto>;
      })
    );
  }

  /**
   * Upload Billing Excel.
   *
   * The API uploads billing excel to azure storage account
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `uploadConsolidatedExcel$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  uploadConsolidatedExcel(params: {
    month?: number;
    year?: number;
    body?: {
      cloudId: string;
      fromCurrencyId?: string;
      comments?: string;
'file': Blob;
}
  }): Observable<SubscriptionMetadataDto> {

    return this.uploadConsolidatedExcel$Response(params).pipe(
      map((r: StrictHttpResponse<SubscriptionMetadataDto>) => r.body as SubscriptionMetadataDto)
    );
  }

  /**
   * Path part for operation activateDeactivateSubscription
   */
  static readonly ActivateDeactivateSubscriptionPath = '/api/v1/subscriptions/{subscriptionId}';

  /**
   * Subscription Status.
   *
   * The API Upates subscription Activate or Deactivate
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `activateDeactivateSubscription()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  activateDeactivateSubscription$Response(params: {
    subscriptionId: string;
    body: CommonId
  }): Observable<StrictHttpResponse<CommonId>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.ActivateDeactivateSubscriptionPath, 'patch');
    if (params) {
      rb.path('subscriptionId', params.subscriptionId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CommonId>;
      })
    );
  }

  /**
   * Subscription Status.
   *
   * The API Upates subscription Activate or Deactivate
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `activateDeactivateSubscription$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  activateDeactivateSubscription(params: {
    subscriptionId: string;
    body: CommonId
  }): Observable<CommonId> {

    return this.activateDeactivateSubscription$Response(params).pipe(
      map((r: StrictHttpResponse<CommonId>) => r.body as CommonId)
    );
  }

  /**
   * Path part for operation reviewExcelProcessing
   */
  static readonly ReviewExcelProcessingPath = '/api/v1/subscription-excel/review/excel-processing';

  /**
   * Review Billing Excel.
   *
   * The API updates the status to review of billing excel
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `reviewExcelProcessing()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewExcelProcessing$Response(params?: {
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.ReviewExcelProcessingPath, 'patch');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Review Billing Excel.
   *
   * The API updates the status to review of billing excel
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `reviewExcelProcessing$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewExcelProcessing(params?: {
  }): Observable<void> {

    return this.reviewExcelProcessing$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateStatusUploadedToInReview
   */
  static readonly UpdateStatusUploadedToInReviewPath = '/api/v1/subscription-excel/in-review/{subscriptionMetaDataId}';

  /**
   * Review Billing Excel Status.
   *
   * The API updates the status uploaded to In Review of billing excel
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateStatusUploadedToInReview()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateStatusUploadedToInReview$Response(params: {
    subscriptionMetaDataId: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.UpdateStatusUploadedToInReviewPath, 'patch');
    if (params) {
      rb.path('subscriptionMetaDataId', params.subscriptionMetaDataId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Review Billing Excel Status.
   *
   * The API updates the status uploaded to In Review of billing excel
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateStatusUploadedToInReview$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateStatusUploadedToInReview(params: {
    subscriptionMetaDataId: string;
  }): Observable<void> {

    return this.updateStatusUploadedToInReview$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation reviewNotOk
   */
  static readonly ReviewNotOkPath = '/api/v1/cloud-excel/review-not-ok';

  /**
   * Review Not Ok Billing Excel.
   *
   * The API updates the status and delete billing excel
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `reviewNotOk()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewNotOk$Response(params: {
    cloudId: string;
    month: number;
    year: number;
    comments: string;
  }): Observable<StrictHttpResponse<SubscriptionMetadataDto>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.ReviewNotOkPath, 'patch');
    if (params) {
      rb.query('cloudId', params.cloudId, {});
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
      rb.query('comments', params.comments, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SubscriptionMetadataDto>;
      })
    );
  }

  /**
   * Review Not Ok Billing Excel.
   *
   * The API updates the status and delete billing excel
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `reviewNotOk$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewNotOk(params: {
    cloudId: string;
    month: number;
    year: number;
    comments: string;
  }): Observable<SubscriptionMetadataDto> {

    return this.reviewNotOk$Response(params).pipe(
      map((r: StrictHttpResponse<SubscriptionMetadataDto>) => r.body as SubscriptionMetadataDto)
    );
  }

  /**
   * Path part for operation getSubscriptionActivityLogListByMetaDataId
   */
  static readonly GetSubscriptionActivityLogListByMetaDataIdPath = '/api/v1/subscription/activity-log/{subscriptionMetaDataId}';

  /**
   * Manage Activity Log List.
   *
   * The API to view the activity log list
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSubscriptionActivityLogListByMetaDataId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSubscriptionActivityLogListByMetaDataId$Response(params: {
    subscriptionMetaDataId: string;
  }): Observable<StrictHttpResponse<Array<ActivityLogDto>>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.GetSubscriptionActivityLogListByMetaDataIdPath, 'get');
    if (params) {
      rb.path('subscriptionMetaDataId', params.subscriptionMetaDataId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ActivityLogDto>>;
      })
    );
  }

  /**
   * Manage Activity Log List.
   *
   * The API to view the activity log list
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getSubscriptionActivityLogListByMetaDataId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSubscriptionActivityLogListByMetaDataId(params: {
    subscriptionMetaDataId: string;
  }): Observable<Array<ActivityLogDto>> {

    return this.getSubscriptionActivityLogListByMetaDataId$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ActivityLogDto>>) => r.body as Array<ActivityLogDto>)
    );
  }

  /**
   * Path part for operation getSubscriptionListByProfileId
   */
  static readonly GetSubscriptionListByProfileIdPath = '/api/v1/profile/{profileId}/subscription';

  /**
   * Manage Subscription List.
   *
   * The API to view the subscription list
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSubscriptionListByProfileId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSubscriptionListByProfileId$Response(params: {
    profileId: string;
    month?: number;
    year?: number;
  }): Observable<StrictHttpResponse<Array<SubscriptionMetadataDto>>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.GetSubscriptionListByProfileIdPath, 'get');
    if (params) {
      rb.path('profileId', params.profileId, {});
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<SubscriptionMetadataDto>>;
      })
    );
  }

  /**
   * Manage Subscription List.
   *
   * The API to view the subscription list
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getSubscriptionListByProfileId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSubscriptionListByProfileId(params: {
    profileId: string;
    month?: number;
    year?: number;
  }): Observable<Array<SubscriptionMetadataDto>> {

    return this.getSubscriptionListByProfileId$Response(params).pipe(
      map((r: StrictHttpResponse<Array<SubscriptionMetadataDto>>) => r.body as Array<SubscriptionMetadataDto>)
    );
  }

  /**
   * Path part for operation getAllSubscriptions
   */
  static readonly GetAllSubscriptionsPath = '/api/v1/cloud-excel';

  /**
   * Get Subscription List.
   *
   * The API to view the subscription list
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllSubscriptions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllSubscriptions$Response(params?: {
    month?: number;
    year?: number;
  }): Observable<StrictHttpResponse<Array<SubscriptionMetadataDto>>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.GetAllSubscriptionsPath, 'get');
    if (params) {
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<SubscriptionMetadataDto>>;
      })
    );
  }

  /**
   * Get Subscription List.
   *
   * The API to view the subscription list
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getAllSubscriptions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllSubscriptions(params?: {
    month?: number;
    year?: number;
  }): Observable<Array<SubscriptionMetadataDto>> {

    return this.getAllSubscriptions$Response(params).pipe(
      map((r: StrictHttpResponse<Array<SubscriptionMetadataDto>>) => r.body as Array<SubscriptionMetadataDto>)
    );
  }

  /**
   * Path part for operation reviewExcelNotOk
   */
  static readonly ReviewExcelNotOkPath = '/api/v1/excel/review-not-ok';

  /**
   * Review Not Ok Billing Excel.
   *
   * The API updates the status and delete billing excel
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `reviewExcelNotOk()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewExcelNotOk$Response(params: {
    orderId: string;
    month: number;
    year: number;
    comments?: string;
  }): Observable<StrictHttpResponse<SubscriptionMetadataDto>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.ReviewExcelNotOkPath, 'patch');
    if (params) {
      rb.query('orderId', params.orderId, {});
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
      rb.query('comments', params.comments, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SubscriptionMetadataDto>;
      })
    );
  }

  /**
   * Review Not Ok Billing Excel.
   *
   * The API updates the status and delete billing excel
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `reviewExcelNotOk$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  reviewExcelNotOk(params: {
    orderId: string;
    month: number;
    year: number;
    comments?: string;
  }): Observable<SubscriptionMetadataDto> {

    return this.reviewExcelNotOk$Response(params).pipe(
      map((r: StrictHttpResponse<SubscriptionMetadataDto>) => r.body as SubscriptionMetadataDto)
    );
  }

    /**
   * Path part for operation editSaasSubscription
   */
  static readonly EditSaasSubscriptionPath = '/api/v1/saas/subscription';

  /**
   * Update Subscription Details.
   *
   * This API is used to update the subscription details.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `editSaasSubscription()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  editSaasSubscription$Response(params: {
    body: SubscriptionDto
  }): Observable<StrictHttpResponse<SubscriptionDto>> {

    const rb = new RequestBuilder(this.rootUrl, InfraBillingService.EditSaasSubscriptionPath, 'patch');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SubscriptionDto>;
      })
    );
  }

  /**
   * Update Subscription Details.
   *
   * This API is used to update the subscription details.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `editSaasSubscription$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  editSaasSubscription(params: {
    body: SubscriptionDto
  }): Observable<SubscriptionDto> {

    return this.editSaasSubscription$Response(params).pipe(
      map((r: StrictHttpResponse<SubscriptionDto>) => r.body as SubscriptionDto)
    );
  }
}
